import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { useAuth } from '../../../Context/AuthContext';
import calendarService from '../../../Services/Api/calendarService';
import { Button } from '../../UI/Buttons';
import { Input, Checkbox } from '../../UI/Inputs';
import { Heading, Text, Label } from '../../UI/Typography';
import { CardContainer, FlexContainer, Divider } from '../../UI/Containers';

const Wrapper = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const Column = styled.div`
  flex: 1 1 360px;
  min-width: 320px;
`;

const HolidayItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
`;

const WeekdayRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const HolidayList = styled.div`
  max-height: 260px;
  overflow-y: auto;
  padding: 8px 8px 8px 12px;
  margin-top: 8px;
  border-radius: ${props => props.theme.borderRadius.small};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundAlt || props.theme.colors.background};
`;

function CompanyCalendar() {
  const { user } = useAuth();
  const companyId = user?.company?.id;

  const [holidays, setHolidays] = useState([]);
  const [weekdays, setWeekdays] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    refresh();
  }, [companyId]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [hRes, wRes] = await Promise.all([
        calendarService.getCompanyHolidays(companyId),
        calendarService.getCompanyWeekdays(companyId),
      ]);
      setHolidays(Array.isArray(hRes) ? hRes : (hRes.data || []));
      setWeekdays(Array.isArray(wRes) ? wRes : (wRes.data || []));
    } catch (err) {
      console.error('Error loading calendar data', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    if (!newDate || !newName) return;
    try {
      await calendarService.createHoliday(companyId, { date: newDate, name: newName });
      setNewDate('');
      setNewName('');
      await refresh();
    } catch (err) {
      console.error('Error creating holiday', err);
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await calendarService.deleteHoliday(id);
      await refresh();
    } catch (err) {
      console.error('Error deleting holiday', err);
    }
  };

  const toggleWeekday = async (weekdayIndex, currentIsWorking) => {
    try {
      await calendarService.updateCompanyWeekday(companyId, weekdayIndex, { is_working: !currentIsWorking });
      await refresh();
    } catch (err) {
      // If weekday configuration does not exist (404), create an initial weekdays set and retry
      console.error('Error updating weekday', err);
      try {
        const status = (err && err.message) || '';
        if (status.includes('404')) {
          // Create a default weekdays array (all working) and flip the clicked one
          const defaultWeekdays = [0,1,2,3,4,5,6].map(i => ({ weekday: i, is_working: i === weekdayIndex ? !currentIsWorking : true }));
          await calendarService.setCompanyWeekdays(companyId, defaultWeekdays);
          await refresh();
        }
      } catch (err2) {
        console.error('Error initializing weekdays then updating', err2);
      }
    }
  };

  const holidayDates = holidays.map(h => new Date(h.date)).filter(d => !isNaN(d.getTime()));

  const nonWorkingDays = (() => {
    // Normalize weekdays to integers 0..6 and collect non-working ones
    const nonWorking = new Set();
    (weekdays || []).forEach(w => {
      const isWorking = w && (w.is_working === true || String(w.is_working) === 'true');
      const raw = w && (w.weekday ?? w.weekDay ?? w.day ?? w.week);
      const n = Number(raw);
      if (!isWorking && Number.isInteger(n) && n >= 0 && n <= 6) nonWorking.add(n);
    });
    return Array.from(nonWorking).sort((a,b) => a-b);
  })();

  return (
    <Wrapper>
      <Column>
        <h3>Company Calendar</h3>
        <div>
          <style>{`
            .rdp-day.holiday{background:#ffe9e9;border-color:#ffbdbd;color:#900;}
            .rdp-day.nonworking{background:#d0d0d0;border-color:#bfbfbf;color:#333;opacity:1}
          `}</style>
          <DayPicker
            mode="single"
            modifiers={{
              holiday: holidayDates,
              nonworking: (date) => nonWorkingDays.includes(date.getDay())
            }}
            modifiersClassNames={{ holiday: 'holiday', nonworking: 'nonworking' }}
          />
          <div style={{ marginTop: 8, fontSize: 13 }}>
            <span style={{ display: 'inline-block', marginRight: 12 }}>
              <span style={{ display: 'inline-block', width: 12, height: 12, background: '#ffe9e9', border: '1px solid #ffbdbd', marginRight: 6 }}></span> Holiday
            </span>
            <span style={{ display: 'inline-block' }}>
              <span style={{ display: 'inline-block', width: 12, height: 12, background: '#d0d0d0', border: '1px solid #bfbfbf', marginRight: 6 }}></span> Non-working
            </span>
          </div>
        </div>
      </Column>

      <Column>
        <h4>Holidays</h4>
        {loading ? <Text>Loading...</Text> : (
          <div>
            <form onSubmit={handleAddHoliday} style={{ marginBottom: 12 }}>
              <FlexContainer gap="sm" responsive>
                <div style={{ minWidth: 160 }}>
                  <Label>Holiday date</Label>
                  <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <Label>Holiday name</Label>
                  <Input placeholder="Holiday name" value={newName} onChange={e => setNewName(e.target.value)} />
                </div>
                <div style={{ alignSelf: 'end' }}>
                  <Button type="submit">Add</Button>
                </div>
              </FlexContainer>
            </form>

            <HolidayList>
              {holidays.length === 0 ? <Text>No holidays defined.</Text> : holidays.map(h => (
                <HolidayItem key={h.holiday_id}>
                  <div>
                    <Text size="sm">{new Date(h.date).toLocaleDateString()}</Text>
                    <div><Text size="sm" weight="semibold">{h.name}</Text></div>
                  </div>
                  <div>
                    <Button variant="outline" size="small" onClick={() => handleDeleteHoliday(h.holiday_id)}>Delete</Button>
                  </div>
                </HolidayItem>
              ))}
            </HolidayList>
          </div>
        )}

        <h4 style={{ marginTop: 20 }}>Weekdays</h4>
        <WeekdayRow>
          {(weekdays.length === 0 ? [0,1,2,3,4,5,6].map(i => ({ weekday: i, is_working: true })) : weekdays).map(w => (
            <label key={w.weekday} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Checkbox checked={!!w.is_working} onChange={() => toggleWeekday(w.weekday, !!w.is_working)} />
              <Text size="sm">{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][w.weekday]}</Text>
            </label>
          ))}
        </WeekdayRow>
      </Column>
    </Wrapper>
  );
}

export default CompanyCalendar;
